import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { Modal, Typography } from '@mui/material'
import { useDispatch } from 'react-redux'
import { ContractorPortalLogout, Logout } from 'src/constants/actionTypes'
import { Button } from '../shared/Button'
import { useNavigate } from 'react-router-dom'
import { CONTRACTOR_PORTAL_ROUTES, CLIENT_ROUTES } from 'src/constants/routes'
import { showToast } from 'src/utils/toast'

interface Props {
  flow?: 'client' | 'contractorPortal'
  isOpen: boolean
  onClose: () => void
}

const LogoutModalComponent = ({ flow, isOpen, onClose }: Props) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const container = useMemo(() => document.getElementById('root'), [])

  const logoutTestIdPrefix = useMemo(() => {
    if (flow === 'contractorPortal') {
      return 'contractors-list'
    }

    if (flow === 'client') {
      return 'customer-interface'
    }

    return 'sign-in'
  }, [flow])

  const handleLogout = useCallback(() => {
    if (flow === 'client') {
      dispatch(Logout.request())
      navigate(CLIENT_ROUTES.LOGGED_OUT_URL)
    }

    if (flow === 'contractorPortal') {
      dispatch(ContractorPortalLogout.request())
      navigate(CONTRACTOR_PORTAL_ROUTES.SIGN_IN_URL)

      showToast('You have been logged out.', { variant: 'warning' })
    }

    onClose()
  }, [flow, onClose])

  return (
    <Modal
      container={container}
      open={Boolean(isOpen)}
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex flex-col justify-end xsm:justify-center items-center backdrop-blur`}
    >
      <div
        css={tw`flex-col p-4 pt-6 bg-shades-0 rounded-2xl rounded-b-none xsm:rounded-b-2xl w-full xsm:w-[390px]`}
      >
        <Typography variant="h2">Log out</Typography>
        <Typography css={tw`mt-2 mb-6 text-shades-400`}>
          Are you sure you want to log out? Your progress will be saved, and you can log in to
          continue where you left off at any time.
        </Typography>
        <Button
          fullWidth
          color="grey"
          className={tw`[&>p]:text-critical-500`}
          onClick={handleLogout}
          data-testid={`${logoutTestIdPrefix}-logout-confirm-btn`}
        >
          Log out
        </Button>
        <Button
          fullWidth
          onClick={onClose}
          color="black"
          className={tw`mt-3`}
          data-testid={`${logoutTestIdPrefix}-logout-cancel-btn`}
        >
          Close
        </Button>
      </div>
    </Modal>
  )
}

export const LogoutModal = memo(LogoutModalComponent)
